import { ColumnType } from 'antd/lib/table';
import Table from 'antd/lib/table/Table';
import * as React from 'react';
import { OrgComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import { IntegrationProviderSlack, UserGroup } from '../../types';
import { displayTextForUserGroup } from '../../helpers/displayHelpers';
import {
    selectOrgGroups,
    selectUserGroupsAndCommsGroupsMapByProviderId,
    selectProviderByProviderId,
} from '../../selectors/selectors';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { antGrey, hexStringToRGBAString, runwayColors } from '../styles/globalStyles';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { SettingOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { CloseOutlined } from '@ant-design/icons';
import { OrgSettingsUpdatable } from '../../actions/updatable';
import { getSlackHandleUIFormat } from '../../helpers';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Alert from 'antd/lib/alert';
import { tryUpdateOrgSettings } from '@/actions/orgSettings';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import Divider from 'antd/lib/divider';

type SlackGroupsDataRow = {
    userGroup: UserGroup;
    slackGroups: string[];
};

interface SlackGroupsModalProps extends SlackGroupsMappingsProps {
    open: boolean;
    onCancel: () => void;
    row?: SlackGroupsDataRow;
}

const SlackGroupsModal: React.FC<SlackGroupsModalProps> = (props: SlackGroupsModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const updateUserGroupsToCommsGroupsMapIsLoading = useIsLoading(
        OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap,
    );
    const updateUserGroupsToCommsGroupsMapError = useError(
        OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap,
    );
    const updateUserGroupsToCommsGroupsMapSuccess = useUpdated(
        OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap,
    );

    const [shouldShowFailedGroupCreationAlert, setShouldShowFailedGroupCreationAlert] = React.useState(false);
    const [notCreatedSlackGroups, setNotCreatedSlackGroups] = React.useState<string[]>([]);
    const [slackGroupsToCreate, setSlackGroupsToCreate] = React.useState<string[]>([]);
    const [createdSlackGroups, setCreatedSlackGroups] = React.useState<string[]>([]);

    const existingUserGroupsToCommsGroupsMap = useSelector((state: RootState) =>
        selectUserGroupsAndCommsGroupsMapByProviderId(state, props.providerId, props.orgId),
    );

    const updateUserGroupsToCommsGroupsMap = (values) => {
        if (!props.row?.userGroup || !props.row?.slackGroups) {
            return;
        }

        const userGroupsToCommsGroupsMapChanges = {
            [props.row.userGroup.id]: values.slackGroups,
        };

        const newSlackHandles = [];
        Object.entries(userGroupsToCommsGroupsMapChanges).map(([_, commsGroups]) => {
            commsGroups = commsGroups.filter(
                (group) => !selectOptions.some((optionGroup) => optionGroup.value === group),
            );
            commsGroups.forEach((str) => newSlackHandles.push(str));
        });
        setSlackGroupsToCreate(newSlackHandles);

        dispatch(
            tryUpdateOrgSettings(OrgSettingsUpdatable.UpdateProviderIdToUserGroupsToCommsGroupsMap, {
                orgId: props.orgId,
                providerId: props.providerId,
                userGroupsToCommsGroupsMap: {
                    ...existingUserGroupsToCommsGroupsMap,
                    ...userGroupsToCommsGroupsMapChanges,
                },
            }),
        );
    };

    React.useEffect(() => {
        if (updateUserGroupsToCommsGroupsMapError) {
            globalMessage.error(updateUserGroupsToCommsGroupsMapError);
        }
    }, [updateUserGroupsToCommsGroupsMapError]);

    React.useEffect(() => {
        if (updateUserGroupsToCommsGroupsMapSuccess) {
            const availableGroups = notificationsProvider?.data?.availableGroups ?? [];
            const availableGroupsHandles = availableGroups.map((group) => group.handle);
            const createdSlackGroupsList = [];
            const notCreatedSlackGroupsList = [];
            let showFailedGroupCreationAlert = false;

            for (const slackGroup in slackGroupsToCreate) {
                if (availableGroupsHandles.includes(slackGroupsToCreate[slackGroup])) {
                    createdSlackGroupsList.push(slackGroupsToCreate[slackGroup]);
                } else {
                    notCreatedSlackGroupsList.push(slackGroupsToCreate[slackGroup]);
                    showFailedGroupCreationAlert = true;
                }
            }

            setCreatedSlackGroups(createdSlackGroupsList);
            setNotCreatedSlackGroups(notCreatedSlackGroupsList);
            setShouldShowFailedGroupCreationAlert(showFailedGroupCreationAlert);

            if (!showFailedGroupCreationAlert) {
                globalMessage.success(
                    `Slack groups for ${displayTextForUserGroup(props.row?.userGroup)} user group updated!`,
                );

                props.onCancel();
            }
        }
    }, [updateUserGroupsToCommsGroupsMapSuccess, props.row]);

    const notificationsProvider = useSelector(
        (state: RootState) =>
            selectProviderByProviderId(state, props.orgId, props.providerId) as IntegrationProviderSlack,
    );

    const availableGroups = notificationsProvider?.data?.availableGroups ?? [];
    const selectOptions = availableGroups
        .sort((a, b) => {
            if (a.handle < b.handle) {
                return -1;
            }
            if (a.handle > b.handle) {
                return 1;
            }
            return 0;
        })
        .map((group) => ({ value: group.handle }))
        .filter((handle) => handle.value.length > 0);

    const tagRender: ({ value, closable }: CustomTagProps) => React.ReactElement = ({
        value,
        closable,
        onClose,
    }: CustomTagProps) => {
        const isExistingGroup = selectOptions.some((group) => group.value === value);
        const tagLabel = isExistingGroup ? getSlackHandleUIFormat(value) : `${getSlackHandleUIFormat(value)} (new)`;

        return (
            <Tag
                color={isExistingGroup ? antGrey.grey3 : hexStringToRGBAString(runwayColors.primary, 0.1)}
                closable={closable}
                closeIcon={<CloseOutlined style={{ color: antGrey.grey7 }} />}
                style={{ marginRight: 3, color: runwayColors.black }}
                onClose={onClose}
            >
                {tagLabel}
            </Tag>
        );
    };

    const slackHandleValidationFunction = (value) => {
        const invalidSlackHandles = [];
        const regex = /^[a-z0-9.\-_]+$/;

        for (const slackHandle of value) {
            if (!regex.test(slackHandle)) {
                invalidSlackHandles.push(slackHandle);
            }
        }

        if (invalidSlackHandles.length > 0) {
            return Promise.reject(`Invalid character(s) in Slack group handle(s): ${invalidSlackHandles.join(', ')}`);
        }

        return Promise.resolve();
    };

    return (
        <Modal
            title={`${displayTextForUserGroup(props.row?.userGroup)} group Slack groups`}
            okButtonProps={{
                loading: updateUserGroupsToCommsGroupsMapIsLoading,
            }}
            onOk={() => {
                setCreatedSlackGroups([]);
                setNotCreatedSlackGroups([]);
                setShouldShowFailedGroupCreationAlert(false);

                form.submit();
            }}
            okText="Save"
            open={props.open}
            onCancel={() => {
                setCreatedSlackGroups([]);
                setNotCreatedSlackGroups([]);
                setShouldShowFailedGroupCreationAlert(false);
                props.onCancel();
            }}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {shouldShowFailedGroupCreationAlert && (
                    <Alert
                        message={`Failed to create group(s): ${notCreatedSlackGroups
                            .map((group) => getSlackHandleUIFormat(group))
                            .join(
                                ', ',
                            )}. Check that your Slack workspace settings allow users other than admins to create user groups. ${
                            createdSlackGroups.length > 0 ?
                                ` Successfully created ${createdSlackGroups
                                    .map((group) => getSlackHandleUIFormat(group))
                                    .join(', ')}.`
                            :   ''
                        }`}
                        type="warning"
                        style={{ marginBottom: '20px' }}
                    />
                )}
                <Form
                    onFinish={updateUserGroupsToCommsGroupsMap}
                    initialValues={{
                        slackGroups: props.row?.slackGroups ?? [],
                    }}
                    form={form}
                    preserve={false}
                    layout="vertical"
                >
                    <Form.Item
                        name="slackGroups"
                        label="Slack group(s)"
                        extra="Runway will create any new group(s) entered above if they don't already exist in Slack. You can manage the new group's membership from Slack at any time."
                        rules={[
                            {
                                validator: (_, value) => {
                                    return slackHandleValidationFunction(value);
                                },
                            },
                        ]}
                    >
                        <Select
                            mode="tags"
                            style={{ width: '100%' }}
                            placeholder="Slack group(s)"
                            tagRender={tagRender}
                        >
                            {selectOptions.map((option) => (
                                <Select.Option key={option.value}>{getSlackHandleUIFormat(option.value)}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

interface SlackGroupsMappingsProps extends OrgComponentProps {
    providerId: string;
    slackTeamName?: string;
}

export const SlackGroupsMappingsTable: React.FC<SlackGroupsMappingsProps> = (props: SlackGroupsMappingsProps) => {
    const slackGroupsMap = useSelector((state: RootState) =>
        selectUserGroupsAndCommsGroupsMapByProviderId(state, props.providerId, props.orgId),
    );

    const orgGroupsById = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));

    const slackGroupsData: SlackGroupsDataRow[] = Object.entries(slackGroupsMap ?? {}) // in case slackGroupsMap is null, pass empty object to prevent crash
        .sort()
        .map(([userGroupId, slackGroups]) => ({
            userGroup: orgGroupsById[userGroupId],
            slackGroups: slackGroups ?? [],
        }));

    const [selectedRow, setSelectedRow] = React.useState<SlackGroupsDataRow>(null);

    const renderColumnTitle = (title: string) => {
        return <Text strong>{title}</Text>;
    };

    const renderSlackGroups = (row: SlackGroupsDataRow) => {
        const slackGroupsString = row.slackGroups?.map((group) => getSlackHandleUIFormat(group)).join(', ') ?? '';

        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flex: 1,
                }}
            >
                {!slackGroupsString ?
                    <Text type="secondary">Not set</Text>
                :   <Text>{slackGroupsString}</Text>}
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAUpdateOrgSettings}
                    onClick={() => {
                        setSelectedRow(row);
                    }}
                    icon={<SettingOutlined style={{ color: antGrey.grey8 }} />}
                    ghost
                    disabled={false}
                />
            </div>
        );
    };

    const columns: ColumnType<SlackGroupsDataRow>[] = [
        {
            title: renderColumnTitle('Runway group'),
            dataIndex: 'userGroup',

            render: (userGroup: UserGroup) => {
                return <Text>{displayTextForUserGroup(userGroup)}</Text>;
            },
            align: 'left',
            width: '50%',
        },
        {
            title: renderColumnTitle('Slack group(s)'),
            render: renderSlackGroups,
            align: 'left',
            width: '50%',
        },
    ];

    return !slackGroupsMap ? null : (
            <>
                <div
                    style={{
                        display: 'flex',
                        gap: 20,
                        alignItems: 'center',
                    }}
                >
                    <Text
                        style={{
                            fontSize: 20,
                            color: antGrey.grey7,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {props.slackTeamName ? `${props.slackTeamName} Slack Groups` : 'Slack Groups'}
                    </Text>
                    <div
                        style={{
                            flex: 1,
                            height: '100%',
                        }}
                    >
                        <Divider
                            type="horizontal"
                            style={{
                                width: '100%',
                                margin: 0,
                            }}
                        />
                    </div>
                </div>
                <Space direction="vertical" size={16} style={{ width: '100%', marginTop: 15 }}>
                    <Space>
                        <Text type="secondary">
                            Configure the Slack groups that correspond to each Runway user group.
                        </Text>
                        <Tooltip
                            title={
                                <div>
                                    Runway can automatically manage Slack group membership for your team based on user
                                    groups in Runway. You can enable this feature by turning on the &apos;Sync Slack
                                    group membership&apos; automation in your app&apos;s Automations settings menu.
                                </div>
                            }
                        >
                            <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                        </Tooltip>
                    </Space>
                    <Table
                        size="middle"
                        columns={columns}
                        dataSource={slackGroupsData}
                        pagination={{ hideOnSinglePage: true, defaultPageSize: 5, size: 'default' }}
                    />
                    <SlackGroupsModal
                        open={!!selectedRow}
                        providerId={props.providerId}
                        row={selectedRow}
                        orgId={props.orgId}
                        onCancel={() => setSelectedRow(null)}
                    />
                </Space>
            </>
        );
};
